import React, { useState } from 'react';
import { useGames } from '../../Context/GameContext';
import { useQuestions } from '../../Context/QuestionContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../Context/AuthContext';
import { Save, ArrowLeft, Search, Plus, AlertCircle } from 'lucide-react';
import { Form, Button, Card, Row, Col, InputGroup, ListGroup, Modal, Badge, Alert } from 'react-bootstrap';
import QuestionFormModal from '../../Components/QuestionFormModal';
import ImageUpload from '../../Components/ImageUpload';

const CreateGame = () => {
  const { addGame } = useGames();
  const { questions, addQuestion } = useQuestions();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isYetkili = user?.isYetkili;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    // Bir oyuncunun bu yarışmada kullanabileceği TOPLAM joker sayısı
    // (yarı yarıya + çift cevap + 2x puan tek havuzdan düşer). 0 = sınırsız.
    joker_limit: 5,
    questions: []
  });
  const [selectedFile, setSelectedFile] = useState(null);

  const [searchQ, setSearchQ] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [errorError, setError] = useState('');

  const toggleQuestion = (qId) => {
     if (formData.questions.includes(qId)) {
        setFormData({ ...formData, questions: formData.questions.filter(id => id !== qId) });
     } else {
        setFormData({ ...formData, questions: [...formData.questions, qId] });
     }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) return;
    
    if (formData.questions.length === 0) {
       setError('Lütfen oyunu kaydetmeden önce en az bir soru ekleyin!');
       return;
    }

    let payload;
    if (selectedFile) {
        const data = new FormData();
        data.append('title', formData.title);
        data.append('description', formData.description);
        data.append('image', selectedFile);
        data.append('is_private', formData.is_private ? 'true' : 'false');
        data.append('joker_limit', String(formData.joker_limit ?? 0));
        // Backend expects JSON string for arrays in FormData
        data.append('questions', JSON.stringify(formData.questions));
        payload = data;
    } else {
        payload = formData;
    }

    console.log("Submitting game payload:", payload);
    try {
        await addGame(payload);
        console.log("Game submitted successfully");
        const Swal = require('sweetalert2');
        Swal.fire({
            title: 'Başarılı!',
            text: 'Oyun başarıyla oluşturuldu.',
            icon: 'success',
            confirmButtonText: 'Tamam'
        }).then(() => {
            navigate('/games');
        });
    } catch (error) {
        console.error("Error submitting game:", error);
        setError("Oyun oluşturulurken bir hata oluştu: " + error.message);
    }
  };

  const handleNewQuestionSaved = async (questionData) => {
     try {
        const newQuestion = await addQuestion(questionData);
        if (newQuestion && newQuestion.id) {
            setFormData(prev => ({ ...prev, questions: [...prev.questions, newQuestion.id] }));
        }
     } catch (err) {
        console.error("Failed to save new question in CreateGame", err);
     }
  };

  const filteredQuestions = questions.filter(q => q.text.toLowerCase().includes(searchQ.toLowerCase()));

  return (
    <div className="py-2 mx-auto" style={{maxWidth: '100%'}}>
      <Button 
        variant="link" 
        onClick={() => navigate('/games')}
        className="text-decoration-none text-muted mb-3 p-0 d-flex align-items-center"
      >
        <ArrowLeft size={18} className="me-2" />
        Oyunlara Dön
      </Button>

      {errorError && (
         <Alert variant="danger" onClose={() => setError('')} dismissible>
            <AlertCircle size={18} className="me-2" />
            {errorError}
         </Alert>
      )}

      <Row className="g-4">
         <Col lg={7}>
            <Card className="custom-card h-100">
               <Card.Body className="p-4">
                  <h4 className="fw-bold mb-4 text-gradient">Oyun Detayları</h4>
                  <Form onSubmit={handleSubmit} id="gameForm">
                     <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold text-secondary">OYUN BAŞLIĞI</Form.Label>
                        <Form.Control
                           type="text"
                           placeholder="Örn: Tarih Yarışması"
                           value={formData.title}
                           onChange={(e) => setFormData({...formData, title: e.target.value})}
                           required
                           className="shadow-none fw-bold"
                        />
                     </Form.Group>

                     <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold text-secondary">AÇIKLAMA</Form.Label>
                        <Form.Control
                           as="textarea"
                           rows={3}
                           placeholder="Oyun hakkında kısa bilgi..."
                           value={formData.description}
                           onChange={(e) => setFormData({...formData, description: e.target.value})}
                           className="shadow-none"
                        />
                     </Form.Group>

                     {isYetkili && (
                         <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold text-secondary">GÖRÜNÜRLÜK (VISIBILITY)</Form.Label>
                            <Form.Select
                               value={formData.is_private ? 'true' : 'false'}
                               onChange={(e) => setFormData({...formData, is_private: e.target.value === 'true'})}
                               className="shadow-none fw-bold"
                            >
                               <option value="false">Herkese Açık (Public)</option>
                               <option value="true">Gizli Yarışma (Sadece bende görünür)</option>
                            </Form.Select>
                         </Form.Group>
                      )}

                     <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold text-secondary">JOKER HAKKI (BU YARIŞMADA)</Form.Label>
                        <Form.Select
                           value={String(formData.joker_limit ?? 0)}
                           onChange={(e) => setFormData({...formData, joker_limit: Number(e.target.value)})}
                           className="shadow-none fw-bold"
                        >
                           <option value="0">Sınırsız</option>
                           {[1,2,3,4,5,6,7,8,9,10].map(n => (
                              <option key={n} value={String(n)}>{n} joker</option>
                           ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                           Bir oyuncunun tüm yarışma boyunca açabileceği toplam joker sayısı.
                           Yarı yarıya, çift cevap ve 2x puan aynı haktan düşer.
                        </Form.Text>
                     </Form.Group>

                     <ImageUpload 
                        value={formData.image} 
                        onChange={(url) => setFormData({...formData, image: url})} 
                        onFileChange={(file) => setSelectedFile(file)}
                     />
                  </Form>
               </Card.Body>
               <Card.Footer className="bg-white border-0 p-4">
                  <Button 
                     type="submit" 
                     form="gameForm" 
                     variant="primary" 
                     size="lg"
                     className="w-100 py-2 fw-bold shadow-sm d-flex align-items-center justify-content-center gap-2 border-0"
                  >
                     <Save size={18} /> Oyunu Kaydet
                  </Button>
               </Card.Footer>
            </Card>
         </Col>

         <Col lg={5}>
            <Card className="custom-card h-100 d-flex flex-column">
               <Card.Header className="bg-white border-0 pt-4 px-4 pb-0 d-flex justify-content-between align-items-center">
                   <div>
                     <h5 className="fw-bold mb-1">Sorular</h5>
                     <p className="text-muted small mb-0">{formData.questions.length} seçildi</p>
                   </div>
                   <Button variant="success" size="sm" className="d-flex align-items-center gap-1" onClick={() => setShowCreateModal(true)}>
                      <Plus size={16} /> Yeni
                   </Button>
               </Card.Header>
               <Card.Body className="p-4 d-flex flex-column h-100">
                  <InputGroup className="mb-3">
                     <InputGroup.Text className="bg-white border-end-0"><Search size={16}/></InputGroup.Text>
                     <Form.Control 
                        placeholder="Soru ara..." 
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        className="border-start-0 shadow-none"
                     />
                  </InputGroup>

                  <div className="flex-fill overflow-auto border rounded bg-white" style={{maxHeight: '500px', minHeight: '300px'}}>
                     <ListGroup variant="flush">
                        {filteredQuestions.map(q => (
                           <ListGroup.Item 
                              key={q.id} 
                              action 
                              onClick={() => toggleQuestion(q.id)}
                              className={`d-flex align-items-center gap-2 border-bottom-0 border-top ${formData.questions.includes(q.id) ? 'bg-primary bg-opacity-10 text-primary' : 'bg-transparent'}`}
                           >
                              <Form.Check 
                                 type="checkbox" 
                                 checked={formData.questions.includes(q.id)}
                                 readOnly
                                 className="pointer-events-none"
                              />
                              <div className="text-truncate small flex-fill">
                                 <div className="fw-medium">{q.text}</div>
                                 <div className="text-muted d-flex gap-2" style={{fontSize: '0.75rem'}}>
                                    <span>{q.difficulty}</span>
                                    {q.media && <span>• Medyalı</span>}
                                 </div>
                              </div>
                           </ListGroup.Item>
                        ))}
                        {filteredQuestions.length === 0 && (
                           <div className="text-center p-5 text-muted">
                              <p className="mb-2">Soru bulunamadı.</p>
                              <Button variant="outline-primary" size="sm" onClick={() => setShowCreateModal(true)}>+ Yeni Soru Ekle</Button>
                           </div>
                        )}
                     </ListGroup>
                  </div>
               </Card.Body>
            </Card>
         </Col>
      </Row>

      {/* CREATE MODAL */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered backdrop="static">
         <Modal.Header closeButton>
            <Modal.Title className="fw-bold">Hızlı Soru Oluştur</Modal.Title>
         </Modal.Header>
         <Modal.Body>
             <Alert variant="info" className="py-2 small mb-3">
                <AlertCircle size={16} className="me-2"/>
                 Pencereyi kapatmak için sağ üstteki <b>X</b> butonunu kullanınız. Dışarı tıklamak pencereyi kapatmaz.
             </Alert>
            <QuestionFormModal onClose={() => setShowCreateModal(false)} onSave={handleNewQuestionSaved} />
         </Modal.Body>
      </Modal>

    </div>
  );
};

export default CreateGame;
